import { faPercent } from "@fortawesome/pro-solid-svg-icons/faPercent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert } from "@mui/lab";
import { AppBar, Card, CardContent, FormControlLabel, FormHelperText, Grid, Switch, TextField, Toolbar } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from "assets/appConfig";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import FormSubtitle from "components/FormSubtitle";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiBox from "components/MuiExtras/MuiBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiCurrencyInput from "components/NumericInput/MuiCurrencyInput";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldNumeric from "components/TextFieldNumeric";
import { Formaters } from "lib/Formarters";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { GetSetupPublicationDto, SaveSetupPublicationDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import Session from "services/Session";
import { ISetupProps } from "views/System/Components/SetupAdminEdit";
import SetupPublicationLogDialog from "views/System/Components/SetupPublicationLogDialog";
import UserAutoComplete from "views/User/Components/UserAutoComplete";
/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IValidation {
    name: boolean;
    thumbnail: boolean;
    minScoreTopicPrediction: boolean;
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    lastError: any,
    validation: IValidation;
    record: Partial<GetSetupPublicationDto>,
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupPublicationEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: null,
            validation: {
                name: null,
                thumbnail: null,
                minScoreTopicPrediction: null
            }
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupPublicationEdit." + r, f);
    }

    ///
    private load() {
        api.getSetupPublication()
            .then((dto) => {
                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    validation: {
                        name: true,
                        thumbnail: true,
                        minScoreTopicPrediction: true
                    }
                }, () => this.props.onEditing(false));
            })
            .catch((error: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed,
                    lastError: error
                });
            });
    }

    ///
    private getPostDto(): SaveSetupPublicationDto {
        const { record } = this.state;

        const postDto = {} as SaveSetupPublicationDto;

        LibUtils.copyProperties(record, postDto);
        postDto.defaultApproverId = record.defaultApprover == null ? null : record.defaultApprover.id;

        return postDto;
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState((state) => {

            switch (name) {
                case "institutionalThumbnail":
                    if (v == null) state.record.thumbnail = null;
                    else state.record.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;

                case "institutionalName":
                    state.record.institutionalName = v;
                    state.validation.name = v.length > 0;
                    break;

                case "thumbnailUploading":
                    state.validation.thumbnail = !v;
                    break;

                case "minScoreTopicPrediction":
                    state.record.minScoreTopicPrediction = v;
                    state.validation.minScoreTopicPrediction = v > 0 && v < 101;
                    break;

                default:
                    state.record[name] = v;
            }

            return state;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private canSave(): boolean {
        const { validation } = this.state;
        return validation.name && validation.thumbnail && validation.minScoreTopicPrediction;
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();


        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupPublication(dto)
            .then((response) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");

                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar} >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.load()}
                            />
                        }
                        <ButtonSave
                            disabled={!this.canSave()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />

                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupPublicationLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormApprover() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("ApprovalAndEditTitle")} />
                    <CardContent>

                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("ApprovalSubtitle")} />

                            <MuiContainer margin="dense">
                                <UserAutoComplete
                                    label={this.res("ApproverLabel")}
                                    emptySelectionLabel={this.res("ApproverSelectLabel")}
                                    placeholder={this.res("ApproverSelectPlaceholder")}
                                    margin="normal"
                                    multiple={false}
                                    selectedItems={record.defaultApprover == null ? null : [record.defaultApprover]}
                                    onChange={(data) => this.updateRecord("defaultApprover", data == null ? null : data[0])}
                                />
                            </MuiContainer>

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="approverOnlySuspectPublication"
                                        checked={record.approverOnlySuspectPublication}
                                        onChange={this.handleChange}
                                        value="approverOnlySuspectPublication"
                                        color="secondary"
                                        size="small"
                                    />
                                }
                                label={
                                    <>
                                        {this.res("ApproverSuspectPublicationL1")}
                                        <br />
                                        <FormHelperText>
                                            {this.res(record.tenantMediaModerationEnabled ? "ApproverSuspectPublicationL2" : "ApproverSuspectPublicationL3")}
                                        </FormHelperText>
                                    </>
                                }
                            />

                        </MuiContainer>

                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("EditionSubtitle")} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="allowManagerEditPublications"
                                        checked={record.allowManagerEditPublications}
                                        onChange={this.handleChange}
                                        value="allowManagerEditPublications"
                                        color="secondary"
                                        size="small"
                                    />
                                }
                                label={<>{this.res("AllowManagerEditL1")}<br /><FormHelperText>{this.res("AllowManagerEditL2")}</FormHelperText> </>}
                            />

                        </MuiContainer>

                    </CardContent>
                </Card>

            </MuiContainer >
        );
    }

    ///
    private renderFormSpecialist() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("SpecialistRulesTitle")} />
                    <CardContent>
                        <p>
                            <MuiMuted>{this.res("SpecialistRulesSubtitle")}</MuiMuted>
                        </p>

                        <Grid container >
                            <Grid item xs={6} >
                                <TextFieldNumeric
                                    name="specialistMinPubs"
                                    label={this.res("SpecialistMinPubsLabel")}
                                    margin="normal"
                                    value={record.specialistMinPubs}
                                    onChange={this.handleChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>

                        <Grid container >
                            <Grid item xs={6}>
                                <TextFieldNumeric
                                    name="specialistMinLikes"
                                    label={this.res("SpecialistMinLikesLabel")}
                                    margin="normal"
                                    value={record.specialistMinLikes}
                                    onChange={this.handleChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>

                        <Grid container >
                            <Grid item xs={6}>
                                <TextFieldNumeric
                                    name="specialistMinThanks"
                                    label={this.res("SpecialistMinThanksLabel")}
                                    margin="normal"
                                    value={record.specialistMinThanks}
                                    onChange={this.handleChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>

                        <br />
                        <MuiMessageBox
                            size="normal"
                            variant="warning"
                            message={this.res("SpecialistRulesMessage")}
                        />

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    private renderFormInstitutional() {
        const { record, validation } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("InstitutionalTitle")} />
                    <CardContent>

                        <MuiContainer margin="normal">
                            <Grid
                                container
                                spacing={3}
                                direction="row"
                            >
                                <Grid item>
                                    <ImageSelection
                                        acceptedFiles={MediaUtils.AcceptImages()}
                                        allowEditSelectedImage={true}
                                        currentImageUrl={record && record.thumbnail ? record.thumbnail.url : null}
                                        dimensions={SquarePicture}
                                        emptyImageUrl={AppImages.DefaultInstitutionalThumbnail}
                                        enableClear={true}
                                        onClear={() => { this.updateRecord("institutionalThumbnail", null); }}
                                        onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                        onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                        onFileUploadSuccess={(data) => this.updateRecord("institutionalThumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                    />

                                </Grid>
                                <Grid item xs>

                                    <MuiContainer margin="normal" >
                                        <TextField
                                            name="institutionalName"
                                            label={this.res("InstitutionalNameLabel")}
                                            autoComplete="off"
                                            value={record.institutionalName}
                                            fullWidth
                                            error={validation.name == false}
                                            inputProps={{ maxLength: 255 }}
                                            onChange={this.handleChange}
                                            margin={"normal"}
                                        />
                                    </MuiContainer >

                                    <MuiBox variant="warning" padding="2x">
                                        <b>{this.res("InstitutionalM1")}</b>
                                        <ul>
                                            <li>{this.res("InstitutionalM1L1")}</li>
                                            <li>{this.res("InstitutionalM1L2")}</li>
                                            <li>{this.res("InstitutionalM1L3")}</li>
                                            <li>{this.res("InstitutionalM1L4")}</li>
                                        </ul>

                                        <b>{this.res("InstitutionalM2")}</b>
                                        <ul>
                                            <li>{this.res("InstitutionalM2L1")}</li>
                                        </ul>
                                    </MuiBox>
                                </Grid>
                            </Grid>


                        </MuiContainer>

                    </CardContent>
                </Card>

            </MuiContainer >
        );
    }


    ///
    private renderFormMachineLearning() {
        const { record, validation } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("MachineLearningTitle")} />

                    <CardContent>

                        <FormSubtitle text={this.res("MachineLearningTopicTitle")} />

                        <Grid container >
                            <Grid item xs={8} >

                                <MuiCurrencyInput
                                    name="minScoreTopicPrediction"
                                    label={this.res("MinScoreTopicPredictionLabel")}
                                    value={record.minScoreTopicPrediction}
                                    error={validation.minScoreTopicPrediction === false}
                                    onChange={(e, v) => this.updateRecord("minScoreTopicPrediction", v)}
                                    precision="0"
                                    locale={Session.locale}
                                    endAdornment={<FontAwesomeIcon icon={faPercent} />}
                                    margin="normal"
                                />

                                {
                                    validation.minScoreTopicPrediction === false &&
                                    <FormHelperText error>{this.res("MinScoreTopicPredictionHelpText")}</FormHelperText>
                                }
                            </Grid>
                        </Grid>
                        {
                            <Alert severity="info">
                                {
                                    record.lastTopicClassificationTraining == null
                                        ? <b>{this.res("TrainingNotExecutedLabel")}</b>
                                        : (
                                            <>
                                                <b>{this.res("LastTrainingLabel")}</b>
                                                <br />
                                                {Formaters.localeDatetime(record.lastTopicClassificationTraining)}
                                                <br />
                                                <br />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            name="resetTopicClassificationTraining"
                                                            checked={record.resetTopicClassificationTraining}
                                                            onChange={this.handleChange}
                                                            value="resetTopicClassificationTraining"
                                                            color="secondary"
                                                            size="small"
                                                        />
                                                    }
                                                    label={this.res("ResetTopicClassificationTrainingLabel")}
                                                />
                                            </>
                                        )
                                }
                            </Alert>
                        }

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />


                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="off" >
                        {this.renderFormApprover()}
                        {this.renderFormInstitutional()}
                        {this.renderFormSpecialist()}
                        {this.renderFormMachineLearning()}
                        {this.renderRecordInfo()}
                    </form>

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupPublicationEdit)));